import { ColorRangeFormatting } from '@ardoq/api-types';
import { type I18nLocale, t } from '@ardoq/i18n';

export type ColorRangePoint = 'min' | 'mid' | 'max';
export type ColorRangePointError = {
  point: ColorRangePoint;
  message: string;
};

/**
 * Validates a color range formatting and returns an error for the first invalid point found.
 * @param rangeValue
 * @param lastTouchedPoint
 */
export const getColorRangePointError = (
  i18nLocale: I18nLocale,
  rangeValue: ColorRangeFormatting,
  lastTouchedPoint?: ColorRangePoint
): ColorRangePointError | undefined => {
  const minVal = rangeValue.min?.value;
  const midVal = rangeValue.mid?.value;
  const maxVal = rangeValue.max?.value;

  const hasMin = typeof minVal === 'number' && Number.isFinite(minVal);
  const hasMid = typeof midVal === 'number' && Number.isFinite(midVal);
  const hasMax = typeof maxVal === 'number' && Number.isFinite(maxVal);

  // Require at least min to validate ordering; max is optional
  if (!hasMin) {
    return;
  }

  // Helper validators for each point to allow targeted checks
  const validateMin = (): ColorRangePointError | undefined => {
    if (hasMid && minVal >= midVal) {
      return {
        point: 'min',
        message:
          minVal === midVal
            ? t(
                i18nLocale,
                'perspectives:colorRangePointError.minEqualsMid',
                "Min and mid point can't be the same"
              )
            : t(
                i18nLocale,
                'perspectives:colorRangePointError.minHigherThanMid',
                "Min point can't be higher than mid point"
              ),
      };
    }
    if (hasMax && minVal >= maxVal) {
      return {
        point: 'min',
        message:
          minVal === maxVal
            ? t(
                i18nLocale,
                'perspectives:colorRangePointError.minEqualsMax',
                "Min and max point can't be the same"
              )
            : t(
                i18nLocale,
                'perspectives:colorRangePointError.minHigherThanMax',
                "Min point can't be higher than max point"
              ),
      };
    }
  };

  const validateMid = (): ColorRangePointError | undefined => {
    if (!hasMid) return;
    if (midVal <= minVal) {
      return {
        point: 'mid',
        message:
          midVal === minVal
            ? t(
                i18nLocale,
                'perspectives:colorRangePointError.minEqualsMid',
                "Min and mid point can't be the same"
              )
            : t(
                i18nLocale,
                'perspectives:colorRangePointError.midLowerThanMin',
                "Mid point can't be lower than min point"
              ),
      };
    }
    if (hasMax && midVal >= maxVal) {
      return {
        point: 'mid',
        message:
          midVal === maxVal
            ? t(
                i18nLocale,
                'perspectives:colorRangePointError.midEqualsMax',
                "Mid and max point can't be the same"
              )
            : t(
                i18nLocale,
                'perspectives:colorRangePointError.midHigherThanMax',
                "Mid point can't be higher than max point"
              ),
      };
    }
  };

  const validateMax = (): ColorRangePointError | undefined => {
    if (!hasMax) return;
    if (hasMid && maxVal <= midVal) {
      return {
        point: 'max',
        message:
          maxVal === midVal
            ? t(
                i18nLocale,
                'perspectives:colorRangePointError.maxEqualsMid',
                "Max point and mid point can't be the same"
              )
            : t(
                i18nLocale,
                'perspectives:colorRangePointError.maxLowerThanMid',
                "Max point can't be lower than the mid point"
              ),
      };
    }
    if (maxVal <= minVal) {
      return {
        point: 'max',
        message:
          maxVal === minVal
            ? t(
                i18nLocale,
                'perspectives:colorRangePointError.maxEqualsMin',
                "Max point and min point can't be the same"
              )
            : t(
                i18nLocale,
                'perspectives:colorRangePointError.maxLowerThanMin',
                "Max point can't be lower than the min point"
              ),
      };
    }
  };

  // 0) If taskTouchedPoint is provided, prioritize validating that point first
  if (lastTouchedPoint) {
    const touchedError =
      lastTouchedPoint === 'max'
        ? validateMax()
        : lastTouchedPoint === 'mid'
          ? validateMid()
          : validateMin();
    if (touchedError) {
      return touchedError;
    }
  }

  // 1) Max (highest priority after touched point)
  const maxError = validateMax();
  if (maxError) return maxError;

  // 2) Mid
  const midError = validateMid();
  if (midError) return midError;

  // 3) Min
  const minError = validateMin();
  if (minError) return minError;
};
